import React, { createContext, useContext, useState, useEffect } from 'react';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing user session on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check authentication status from localStorage
  const checkAuthStatus = () => {
    try {
      const savedUser = localStorage.getItem('kingsolarman_user');
      const savedToken = localStorage.getItem('kingsolarman_token');
      
      if (savedUser && savedToken) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setIsAuthenticated(true);
        console.log('✅ User session restored:', userData.email);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      // Clear corrupted data
      localStorage.removeItem('kingsolarman_user');
      localStorage.removeItem('kingsolarman_token');
    } finally {
      setIsLoading(false);
    }
  };

  // User registration
  const register = async (userData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call (replace with real API later)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate user ID
      const userId = `USER_${Date.now()}`;
      const emailVerificationToken = `VERIFY_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create user object
      const newUser = {
        id: userId,
        name: `${userData.firstName} ${userData.lastName}`,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        city: userData.city,
        postalCode: userData.postalCode,
        isVerified: false,
        verificationToken: emailVerificationToken,
        registrationDate: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      };

      // Save to localStorage
      localStorage.setItem('kingsolarman_user', JSON.stringify(newUser));
      localStorage.setItem('kingsolarman_token', `TOKEN_${userId}_${Date.now()}`);
      
      // Store pending verification
      localStorage.setItem('kingsolarman_pending_verification', JSON.stringify({
        email: userData.email,
        token: emailVerificationToken,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      }));

      // Send verification email (simulate)
      await simulateEmailVerification(newUser.email, emailVerificationToken);

      setUser(newUser);
      setIsAuthenticated(true);

      console.log('✅ User registered successfully:', newUser.email);
      return { success: true, user: newUser, needsVerification: true };

    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // User login
  const login = async (credentials) => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call (replace with real API later)
      await new Promise(resolve => setTimeout(resolve, 800));

      // Check if user exists in localStorage
      const registeredUsers = JSON.parse(localStorage.getItem('kingsolarman_users') || '[]');
      const existingUser = registeredUsers.find(u => u.email === credentials.email);

      if (!existingUser) {
        throw new Error('User not found. Please register first.');
      }

      // Verify password (in real app, this would be hashed)
      if (existingUser.password !== credentials.password) {
        throw new Error('Invalid password.');
      }

      // Check if email is verified
      if (!existingUser.isVerified) {
        return { 
          success: false, 
          needsVerification: true, 
          email: existingUser.email,
          verificationToken: existingUser.verificationToken 
        };
      }

      // Update last login
      existingUser.lastLogin = new Date().toISOString();

      // Save to localStorage
      localStorage.setItem('kingsolarman_user', JSON.stringify(existingUser));
      localStorage.setItem('kingsolarman_token', `TOKEN_${existingUser.id}_${Date.now()}`);

      setUser(existingUser);
      setIsAuthenticated(true);

      console.log('✅ User logged in successfully:', existingUser.email);
      return { success: true, user: existingUser };

    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Email verification
  const verifyEmail = async (email, token) => {
    try {
      const pendingVerification = JSON.parse(
        localStorage.getItem('kingsolarman_pending_verification') || '{}'
      );

      if (pendingVerification.email === email && pendingVerification.token === token) {
        // Check if verification hasn't expired
        if (new Date(pendingVerification.expiresAt) > new Date()) {
          // Mark user as verified
          const userData = JSON.parse(localStorage.getItem('kingsolarman_user') || '{}');
          if (userData.email === email) {
            userData.isVerified = true;
            delete userData.verificationToken;
            localStorage.setItem('kingsolarman_user', JSON.stringify(userData));
            localStorage.removeItem('kingsolarman_pending_verification');
            
            setUser(userData);
            console.log('✅ Email verified successfully:', email);
            return { success: true, user: userData };
          }
        } else {
          throw new Error('Verification token has expired.');
        }
      } else {
        throw new Error('Invalid verification token.');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('kingsolarman_user');
    localStorage.removeItem('kingsolarman_token');
    localStorage.removeItem('kingsolarman_pending_verification');
    
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    
    console.log('👋 User logged out successfully');
  };

  // Update user profile
  const updateProfile = (updatedData) => {
    if (!user) return;

    const updatedUser = { ...user, ...updatedData };
    localStorage.setItem('kingsolarman_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    
    console.log('✅ User profile updated:', updatedUser.email);
  };

  // Simulate email verification (replace with real email service)
  const simulateEmailVerification = async (email, token) => {
    console.log(`📧 Simulating email verification for ${email}`);
    console.log(`Verification token: ${token}`);
    
    // In real implementation, this would call the email service
    // For now, we just log the verification details
    return true;
  };

  // Get user orders
  const getUserOrders = () => {
    if (!user) return [];
    
    const allOrders = JSON.parse(localStorage.getItem('paypal_orders') || '[]');
    return allOrders.filter(order => 
      order.payer?.email_address === user.email || 
      order.customerInfo?.email === user.email
    );
  };

  // Context value
  const value = {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    
    // Actions
    register,
    login,
    logout,
    verifyEmail,
    updateProfile,
    checkAuthStatus,
    getUserOrders,
    
    // Utilities
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected Route Component
export const ProtectedRoute = ({ children, requireAuth = true }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return (
      <div className="auth-required">
        <h2>Authentication Required</h2>
        <p>Please log in to access this page.</p>
        <button 
          className="btn btn-primary"
          onClick={() => window.location.href = '/'}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return children;
};

export default AuthContext;
